import { createContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { isTokenExpired, login, logout, requestPasswordReset, confirmPasswordReset } from '../services/authService';
import type { LoginResult } from '../../../types/types';
import { getDecryptedToken, storeEncryptedToken, clearToken } from '../../../services/tokenStorage';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  loading: boolean;
  requestPasswordReset: (username: string) => Promise<any>;
  confirmPasswordReset: (username: string, code: string, newPassword: string) => Promise<any>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // On mount, check token expiration
  useEffect(() => {
    const storedToken = getDecryptedToken('accessToken');
    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken);
      setIsAuthenticated(true);
    } else {
      doLogout();
    }
    setLoading(false);
  }, []); // Only runs once on initial load

  // Handle token expiration based on the token's expiration time
  useEffect(() => {
    if (!token) return;

    const [, payloadBase64] = token.split('.');
    const payload = JSON.parse(atob(payloadBase64));
    const expiry = payload.exp;
    const now = Math.floor(Date.now() / 1000);
    const msUntilExpiry = (expiry - now) * 1000;

    if (msUntilExpiry > 0) {
      const timeout = setTimeout(() => {
        doLogout('Session expired. Please log in again.');
      }, msUntilExpiry);

      return () => clearTimeout(timeout);
    } else {
      doLogout('Session expired. Please log in again.');
    }
  }, [token]); // Runs when token changes (e.g., after login)

  // Login function to fetch the token and authenticate
  const doLogin = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const result = await login(username, password);
      if (result && 'token' in result) {
        storeEncryptedToken('accessToken', result.token ?? '');
        setToken(result.token ?? null);
        setIsAuthenticated(true);
      }
      return result;
    } catch (err) {
      throw err;
    }
  };

  // Logout function to clear the session and navigate
  const doLogout = (message?: string) => {
    logout(message); // Clears session
    clearToken('accessToken');
    setToken(null);
    setIsAuthenticated(false);
    navigate('/', {
      state: { message: typeof message === 'string' ? message : undefined },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        login: doLogin,
        logout: doLogout,
        loading,
        requestPasswordReset,
        confirmPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
